<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import ContactEditableField from './ContactEditableField.vue';
import ContactCustomAttributeItem from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCustomAttributeItem.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    ContactEditableField,
    ContactCustomAttributeItem,
    NextButton,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      showAllCustomAttributes: false,
    };
  },
  computed: {
    ...mapGetters({
      contactAttributeDefinitions: 'attributes/getContactAttributes',
    }),
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    customAttributeRows() {
      const customAttributes =
        this.contact?.customAttributes || this.contact?.custom_attributes || {};

      return (this.contactAttributeDefinitions || [])
        .map(attribute => {
          return {
            ...attribute,
            value: customAttributes[attribute.attributeKey] ?? '',
          };
        })
        .filter(Boolean);
    },
    visibleCustomAttributeRows() {
      if (this.showAllCustomAttributes) {
        return this.customAttributeRows;
      }

      return this.customAttributeRows.slice(0, 1);
    },
    hasHiddenCustomAttributes() {
      return this.customAttributeRows.length > 1;
    },
    customAttributesToggleLabel() {
      return this.showAllCustomAttributes
        ? this.$t('CUSTOM_ATTRIBUTES.SHOW_LESS')
        : this.$t('CUSTOM_ATTRIBUTES.SHOW_MORE');
    },
  },
  methods: {
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    toggleCustomAttributes() {
      this.showAllCustomAttributes = !this.showAllCustomAttributes;
    },
    async updateContactField(field, value) {
      try {
        await this.$store.dispatch('contacts/update', {
          id: this.contact.id,
          [field]: value,
        });
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(error?.data || this.$t('CONTACT_FORM.ERROR_MESSAGE'));
      }
    },
    async updateAdditionalAttribute(field, value) {
      try {
        await this.$store.dispatch('contacts/update', {
          id: this.contact.id,
          additional_attributes: {
            ...this.additionalAttributes,
            [field]: value,
          },
        });
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(error?.data || this.$t('CONTACT_FORM.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full divide-y divide-n-weak/60">
    <p
      v-if="additionalAttributes.description"
      class="px-3 py-2.5 text-sm break-words text-n-slate-11"
    >
      {{ additionalAttributes.description }}
    </p>
    <ContactEditableField
      :value="contact.email"
      :href="contact.email ? `mailto:${contact.email}` : ''"
      icon="i-lucide-mail"
      input-type="email"
      label="E-mail"
      show-copy
      @update="value => updateContactField('email', value)"
    />
    <ContactEditableField
      v-if="contact.identifier"
      :value="contact.identifier"
      icon="i-lucide-id-card"
      :label="$t('CONTACT_PANEL.IDENTIFIER')"
      :editable="false"
    />
    <ContactEditableField
      :value="additionalAttributes.company_name"
      icon="i-lucide-building-2"
      label="Empresa"
      @update="value => updateAdditionalAttribute('company_name', value)"
    />
    <ContactEditableField
      v-if="location || additionalAttributes.location"
      :value="location || additionalAttributes.location"
      icon="i-lucide-map-pin"
      :label="$t('CONTACT_PANEL.LOCATION')"
      :editable="false"
    />
    <ContactCustomAttributeItem
      v-for="attribute in visibleCustomAttributeRows"
      :key="attribute.attributeKey"
      :attribute="attribute"
      :contact-id="contact.id"
      is-editing-view
      compact
      class="w-full"
    />
    <NextButton
      v-if="hasHiddenCustomAttributes"
      ghost
      xs
      slate
      class="self-start mx-3 my-2"
      :icon="
        showAllCustomAttributes
          ? 'i-lucide-chevron-up'
          : 'i-lucide-chevron-down'
      "
      :label="customAttributesToggleLabel"
      @click="toggleCustomAttributes"
    />
  </div>
</template>
